/** biome-ignore-all lint/a11y/noStaticElementInteractions: Baseline */
/** biome-ignore-all lint/a11y/useAriaPropsSupportedByRole: Baseline */
import "./AutocompleteInput.css";
import {
  autoPlacement,
  autoUpdate,
  offset,
  size,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useListNavigation,
  useRole,
} from "@floating-ui/react";
import cn from "classnames";
import {
  type ComponentProps,
  type CSSProperties,
  createElement,
  type FocusEventHandler,
  useRef,
  useState,
} from "react";
import type { IconType } from "react-icons";
import { TbLoader2 } from "react-icons/tb";
import { Input } from "@/components/form/Input";

export type Option<Value extends string | number> = {
  icon?: IconType;
  label: string;
  value: Value;
};

interface AutocompleteInputProps<Value extends string | number>
  extends Omit<ComponentProps<typeof Input>, "onSelect"> {
  options: Option<Value>[];
  onSelect?: (value: Option<Value>) => unknown;
  loading?: boolean;
}

export function AutocompleteInput<Value extends string | number>(
  props: AutocompleteInputProps<Value>,
) {
  const {
    error,
    className,
    options,
    loading = false,
    onSelect,
    ...rest
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const { refs, floatingStyles, context } = useFloating<HTMLInputElement>({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "bottom",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      autoPlacement(),
      size({
        apply({ elements, rects }) {
          elements.floating.style.maxWidth = `${rects.reference.width}px`;
        },
      }),
    ],
  });

  const listRef = useRef<(HTMLDivElement | null)[]>([]);

  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    loop: true,
  });

  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "listbox" });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [
      //
      listNavigation,
      focus,
      dismiss,
      role,
    ],
  );

  const handleSelect = (value: Option<Value>) => {
    onSelect?.(value);
    const referenceElem = refs.reference.current;
    if (referenceElem instanceof HTMLInputElement) {
      referenceElem.focus(); // Focus input so tab works correctly
    }

    setIsOpen(false);
  };

  const { onBlur: referenceOnBlur, ...referenceProps } = getReferenceProps();

  return (
    <div onBlur={referenceOnBlur as FocusEventHandler}>
      <Input ref={refs.setReference} {...rest} {...referenceProps} />
      {isOpen && options.length > 0 && (
        <div
          tabIndex={-1}
          ref={refs.setFloating}
          className="options"
          style={floatingStyles as CSSProperties}
          {...getFloatingProps()}
        >
          {loading ? (
            <div className="loading">
              <TbLoader2 size={20} className="icon spin" />
              Loading...
            </div>
          ) : (
            options.map((option, index) => (
              <div
                key={option.value}
                className={cn("option", {
                  active: activeIndex === index,
                })}
                tabIndex={index === activeIndex ? 0 : -1}
                aria-selected={false}
                ref={(node) => {
                  listRef.current[index] = node;
                }}
                {...getItemProps({
                  // Handle pointer select.
                  onClick() {
                    handleSelect(option);
                  },
                  // Handle keyboard select.
                  onKeyDown(event) {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSelect(option);
                    }
                  },
                })}
              >
                {option.icon && createElement(option.icon, { size: 20 })}
                {option.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
